import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, todayIso } from '@platform/core/lib/format';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import type { IInboxEntry } from './IInboxEntry';
import type { IInboxText } from './IInboxText';
import { useSince } from './useSince';

const DAY_MS = 86400000;
/** Довжина уривка коментаря в тексті (прототип v4: slice(0, 140)). */
const EXCERPT = 140;

/**
 * Текст елемента вхідних мовою інтерфейсу у форматі прототипу v4, складений з виду й конверта запису (запис — голим номером, як у
 * прототипі): нагадування — «Прострочено: 3 · назва» / «Строк 28 жовт.: 3 · назва» з підписом «Прострочено N дн.» / «Строк 28 жовт.»;
 * коментар — «Хто прокоментував(-ла) 3: «уривок»»; доручення — «Хто призначив(-ла) вам 3 · назва»; решта (оновлення, збережені
 * сповіщення) — текст сервера з номером замість коду запису, без тексту — «номер · назва». Підпис не-нагадувань — відносний час.
 */
export function useInboxText(): (entry: IInboxEntry) => IInboxText {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const since = useSince();
  return useCallback((e: IInboxEntry) => {
    const code = e.number ?? e.display;
    const record = [code, e.title].filter(Boolean).join(' · ');
    const date = formatDate(e.due, lang);
    let text = e.text && e.display && code ? e.text.replace(e.display, code) : e.text || record;
    if (e.kind === 'reminder' && e.display) text = e.late ? t('inboxPage.text.overdue', { record }) : t('inboxPage.text.due', { date, record });
    if (e.kind === 'comment' && e.actor && e.display) {
      const excerpt = (e.excerpt ?? '').trim();
      text = excerpt
        ? t('notifText.commented', { who: e.actor, code, excerpt: excerpt.length > EXCERPT ? `${excerpt.slice(0, EXCERPT)}…` : excerpt })
        : t('notifText.commentedShort', { who: e.actor, code });
    }
    if (e.kind === 'assignment' && e.actor && e.display) text = t('notifText.assigned', { who: e.actor, record });
    if (e.kind !== 'reminder' || !e.due) return { text, meta: since(e.at) };
    const days = Math.max(1, Math.round((Date.parse(`${todayIso()}T00:00:00Z`) - Date.parse(`${e.due}T00:00:00Z`)) / DAY_MS));
    return { text, meta: e.late ? t('inboxPage.meta.overdue', { count: days }) : t('inboxPage.meta.due', { date }) };
  }, [t, lang, since]);
}
